import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { EmptyState } from "../../components/EmptyState";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime, formatMoney, formatNumber, normalizeText } from "../../lib/format";
import { lastOrderPullAt, orderRows } from "../../lib/selectors";
import type { AppData, DrawerState, OrderRow } from "../../lib/types";

type OrdersPageProps = {
  data: AppData;
  drawer: DrawerState;
  setDrawer: (drawer: DrawerState) => void;
};

export function OrdersPage({ data, drawer, setDrawer }: OrdersPageProps) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const syncStarted = useRef(false);
  const syncOrders = useMutation({
    mutationFn: api.syncOrders,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  useEffect(() => {
    if (syncStarted.current || !data.status?.cafe24Connected) return;
    syncStarted.current = true;
    syncOrders.mutate();
  }, [data.status?.cafe24Connected, syncOrders]);

  const orders = orderRows(data);
  const activeView = params.get("view") || "all";
  const views: SavedView[] = [
    { key: "all", label: "전체", count: orders.length },
    { key: "payment", label: "주문/결제", count: orders.filter((order) => isOrderView(order, "payment")).length },
    { key: "shipping", label: "배송/출고", count: orders.filter((order) => isOrderView(order, "shipping")).length },
    { key: "cancelled", label: "취소", count: orders.filter((order) => isOrderView(order, "cancelled")).length },
    { key: "return_exchange", label: "반품/교환", count: orders.filter((order) => isOrderView(order, "return_exchange")).length },
    { key: "needs_check", label: "확인 필요", count: orders.filter((order) => isOrderView(order, "needs_check")).length }
  ];
  const filtered = orders.filter((order) => {
    const itemText = orderItems(order)
      .flatMap((item) => [item.productName, item.product_name, item.sku, item.readableAlias, item.optionName, item.variantName])
      .filter(Boolean)
      .join(" ");
    const text = normalizeText([order.orderIdMasked, order.orderKey, order.customerLabel, order.channelName, order.statusBucket, itemText].join(" "));
    if (query && !text.includes(normalizeText(query))) return false;
    return isOrderView(order, activeView);
  });
  const selectedId = drawer?.type === "order" ? String(drawer.record.orderKey || drawer.record.orderIdMasked || "") : "";
  const columns = useMemo<ColumnDef<OrderRow>[]>(() => [
    {
      accessorKey: "orderIdMasked",
      header: "주문",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.orderIdMasked || row.original.orderKey || "-"}</strong>
          <span>{row.original.channelName || row.original.channelCode || "온라인몰"}</span>
        </div>
      ),
      size: 220
    },
    { accessorKey: "orderedAt", header: "주문일", cell: ({ row }) => formatDateTime(row.original.orderedAt) },
    { accessorKey: "customerLabel", header: "고객", cell: ({ row }) => row.original.customerLabel || "-" },
    { id: "items", header: "상품", cell: ({ row }) => orderItemSummary(row.original) },
    { accessorKey: "totalAmount", header: "금액", cell: ({ row }) => formatMoney(orderAmount(row.original)) },
    {
      id: "nextAction",
      header: "다음 작업",
      cell: ({ row }) => <StatusBadge tone={nextActionTone(row.original)}>{nextActionLabel(row.original)}</StatusBadge>,
      size: 130
    },
    {
      accessorKey: "statusBucket",
      header: "상태",
      cell: ({ row }) => <StatusBadge tone={statusTone(row.original.statusBucket)}>{orderStatusLabel(row.original.statusBucket)}</StatusBadge>
    }
  ], []);

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <div className="panel-head">
          <div>
            <h2>온라인몰 주문</h2>
            <p>주문 상태를 읽고 출고, 취소, 반품/교환 확인이 필요한 건을 처리합니다.</p>
          </div>
          <StatusBadge tone={syncOrders.isPending ? "info" : syncOrders.error ? "warning" : "success"}>
            {syncOrders.isPending ? "자동 읽기 중" : syncOrders.error ? "자동 읽기 확인 필요" : `${data.status?.cafe24Connected ? "Cafe24 자동 읽기" : "Cafe24 연결 필요"}`}
          </StatusBadge>
        </div>
        <div className="summary-strip">
          <div>
            <span>전체 주문</span>
            <strong>{formatNumber(orders.length)}건</strong>
          </div>
          <div>
            <span>출고 확인</span>
            <strong>{formatNumber(orders.filter((order) => isOrderView(order, "shipping")).length)}건</strong>
          </div>
          <div>
            <span>취소/반품</span>
            <strong>{formatNumber(orders.filter((order) => isOrderView(order, "cancelled") || isOrderView(order, "return_exchange")).length)}건</strong>
          </div>
          <div>
            <span>확인 필요</span>
            <strong>{formatNumber(orders.filter((order) => isOrderView(order, "needs_check")).length)}건</strong>
          </div>
        </div>
        <SavedViews views={views} active={activeView} onChange={(view) => setParams(view === "all" ? {} : { view })} />
        <div className="toolbar-row">
          <label className="search-field">
            검색
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="주문번호, 고객, 상품, 상태" />
          </label>
          <div className="meta-strip">
            <span>최근 확인</span>
            <strong>{lastOrderPullAt(data) ? formatDateTime(lastOrderPullAt(data)) : "-"}</strong>
          </div>
        </div>
      </section>
      <section className="panel">
        {orders.length ? (
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={(row) => String(row.orderKey || row.orderIdMasked || "")}
            selectedId={selectedId}
            onSelect={(record) => setDrawer({ type: "order", record })}
            emptyTitle="필터에 맞는 온라인몰 주문이 없습니다."
          />
        ) : (
          <EmptyState title="표시할 온라인몰 주문이 없습니다." body="Cafe24에 새 주문이 들어오면 주문, 취소, 반품/교환 확인 건이 이 화면에 표시됩니다." />
        )}
      </section>
    </div>
  );
}

function orderItems(order: OrderRow) {
  return Array.isArray(order.items) ? order.items : [];
}

function orderAmount(order: OrderRow): number {
  return Number(order.totalAmount ?? order.paymentAmount ?? order.orderAmount ?? order.actualPaymentAmount ?? 0);
}

function orderQuantity(order: OrderRow): number {
  return orderItems(order).reduce((total, item) => total + Number(item.quantity ?? item.qty ?? item.orderQuantity ?? 0), 0);
}

function orderItemSummary(order: OrderRow): string {
  const lineCount = orderItems(order).length;
  const quantity = orderQuantity(order);
  if (!lineCount) return "0라인";
  return quantity ? `${formatNumber(lineCount)}라인 · ${formatNumber(quantity)}개` : `${formatNumber(lineCount)}라인`;
}

function nextActionLabel(order: OrderRow): string {
  const bucket = String(order.statusBucket || "").toLowerCase();
  if (bucket.includes("cancel")) return "취소 확인";
  if (bucket.includes("return") || bucket.includes("exchange")) return "반품/교환 확인";
  if (bucket.includes("check") || bucket.includes("hold") || bucket.includes("issue")) return "확인 필요";
  if (bucket.includes("ship") || bucket.includes("ready") || bucket.includes("pick") || bucket.includes("pack")) return "출고 확인";
  if (bucket.includes("paid") || bucket.includes("payment") || bucket.includes("ordered") || bucket.includes("order")) return "출고 준비";
  return "주문 확인";
}

function nextActionTone(order: OrderRow): "default" | "success" | "warning" | "danger" | "info" {
  const bucket = String(order.statusBucket || "").toLowerCase();
  if (bucket.includes("cancel") || bucket.includes("return") || bucket.includes("exchange")) return "danger";
  if (bucket.includes("check") || bucket.includes("hold") || bucket.includes("issue")) return "warning";
  if (bucket.includes("ship") || bucket.includes("ready") || bucket.includes("pick") || bucket.includes("pack")) return "info";
  return "default";
}

function isOrderView(order: OrderRow, view: string): boolean {
  const bucket = String(order.statusBucket || "").toLowerCase();
  if (view === "all") return true;
  if (view === "payment") return ["ordered", "payment", "paid", "order"].some((value) => bucket.includes(value));
  if (view === "shipping") return ["shipping", "ship", "ready", "picked", "packed"].some((value) => bucket.includes(value));
  if (view === "cancelled") return bucket.includes("cancel");
  if (view === "return_exchange") return bucket.includes("return") || bucket.includes("exchange");
  if (view === "needs_check") return bucket.includes("check") || bucket.includes("hold") || bucket.includes("issue");
  return true;
}

function orderStatusLabel(status?: string): string {
  const bucket = String(status || "");
  if (!bucket) return "확인";
  return {
    ordered: "주문/결제",
    payment: "주문/결제",
    shipping: "배송/출고",
    cancelled: "취소",
    return_exchange: "반품/교환",
    needs_check: "확인 필요"
  }[bucket] || bucket;
}

function statusTone(status?: string): "default" | "success" | "warning" | "danger" | "info" {
  const bucket = String(status || "").toLowerCase();
  if (bucket.includes("cancel") || bucket.includes("return")) return "danger";
  if (bucket.includes("check") || bucket.includes("hold")) return "warning";
  if (bucket.includes("ship")) return "info";
  return "default";
}
